import { supabase } from '@/core/supabase/client';

export interface OrganizationMembership {
  organization_id: string;
  name: string;
  kind: 'solo' | 'team';
  role: 'admin' | 'member';
  is_admin: boolean;
}

export interface PendingInvitation {
  invitation_id: string;
  organization_id: string;
  organization_name: string;
  role: 'admin' | 'member';
  invited_at: string;
}

/** Organizaciones donde el usuario es miembro ACTIVO. La fuente es RLS, no la UI. */
export async function fetchMyOrganizations(): Promise<OrganizationMembership[]> {
  const { data, error } = await supabase.rpc('my_organizations');
  if (error) throw error;
  return (data ?? []) as OrganizationMembership[];
}

/** Lo que el asistente de arranque fue preguntando. */
export interface AltaDeVeterinaria {
  name: string;
  kind: 'solo' | 'team';
  /** Con este nombre va a figurar en los repartos. No es el de la veterinaria. */
  my_display_name: string;
  charge_mobility: boolean;
  mobility_mode: 'none' | 'per_km_fuel' | 'per_km_fixed' | 'flat' | 'manual';
  mobility_rate_cents?: number | null;
  flat_mobility_cents?: number | null;
  /** El precio del novillo del mes. Sin él la aplicación anda: los importes quedan pendientes. */
  steer_price_cents?: number | null;
  fuel_price_cents?: number | null;
  price_year?: number;
  price_month?: number;
}

/**
 * Da de alta una veterinaria y deja a quien la crea listo para trabajar.
 *
 * Una sola llamada y una sola transacción. Si algo falla no queda nada a medio
 * crear: una organización sin participante ni ajustes deja a su dueño adentro
 * de una aplicación rota y sin salida, que es peor que no haberla creado.
 */
export async function crearOrganizacion(datos: AltaDeVeterinaria): Promise<string> {
  const { data, error } = await supabase.rpc('create_organization', { p: datos });
  if (error) throw error;
  return data as string;
}

export interface OrgUser {
  profile_id: string;
  name: string;
}

/**
 * Miembros de la organización, para filtrar el listado por "quién lo cargó".
 * Depende de las policies de `organization_members` y `profiles`: lo que RLS no
 * deja ver, acá tampoco aparece.
 */
export async function listOrgUsers(organizationId: string): Promise<OrgUser[]> {
  const { data, error } = await supabase
    .from('organization_members')
    .select('profile_id, profile:profiles(full_name, email)')
    .eq('organization_id', organizationId)
    .neq('status', 'invited');
  if (error) throw error;

  return (data ?? [])
    .map((row) => {
      // PostgREST tipa la relación como arreglo aunque sea 1:1.
      const r = row as unknown as {
        profile_id: string;
        profile: { full_name: string | null; email: string } | null;
      };
      return {
        profile_id: r.profile_id,
        name: r.profile?.full_name || r.profile?.email || 'Sin nombre',
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

export async function fetchMyInvitations(): Promise<PendingInvitation[]> {
  const { data, error } = await supabase.rpc('my_pending_invitations');
  if (error) throw error;
  return (data ?? []) as PendingInvitation[];
}

export async function acceptInvitation(invitationId: string): Promise<void> {
  const { error } = await supabase.rpc('accept_invitation', { p_invitation: invitationId });
  if (error) throw error;
}

/**
 * Qué pasó con la invitación.
 *
 * Son dos cosas distintas y hay que decirlas distinto. Antes esto devolvía
 * `void` y la pantalla anunciaba «Invitación enviada» con sólo haber insertado
 * una fila: no se mandaba ningún correo, y quien invitaba se quedaba esperando
 * una respuesta que nunca iba a llegar.
 */
export type ResultadoInvitacion =
  /** La invitación quedó creada y el correo salió. */
  | { resultado: 'enviada'; email: string }
  /**
   * La invitación quedó creada —y sirve— pero el aviso no salió. Quien invitó
   * tiene que enterarse para avisar por otro medio.
   */
  | { resultado: 'creada_sin_aviso'; motivo: string }
  /** No se pudo ni crear. `etapa` dice dónde se cortó. */
  | { resultado: 'error'; etapa: string; detalle?: string };

export async function inviteUser(input: {
  organizationId: string;
  email: string;
  role: 'admin' | 'member';
  // El caso `error` no aparece acá porque se lanza: quien llama se ocupa de
  // los dos desenlaces que dejan la invitación creada, y de nada más.
}): Promise<Exclude<ResultadoInvitacion, { resultado: 'error' }>> {
  // Del lado del servidor: mandar el correo necesita la llave de servicio, que
  // no puede viajar al teléfono.
  const { data, error } = await supabase.functions.invoke('invitar-persona', {
    body: {
      organization_id: input.organizationId,
      email: input.email,
      role: input.role,
      redirect_to: window.location.origin,
    },
  });
  if (error) {
    // `functions.invoke` informa cualquier corte de red con un mismo texto:
    // «Failed to send a request to the Edge Function». Eso mandó a buscar la
    // función caída cuando lo que faltaba era el permiso CORS, y va a volver a
    // pasar con el teléfono sin señal. Se traduce a algo que se pueda accionar
    // sin perder el original, que es lo único que sirve para diagnosticar.
    const crudo = (error as Error).message;
    throw new Error(
      /failed to send a request/i.test(crudo)
        ? 'No se pudo contactar al servidor. Revisá la conexión y probá de nuevo.'
        : crudo,
    );
  }
  const r = data as ResultadoInvitacion;
  if (r.resultado === 'error') {
    throw new Error(r.detalle ?? `No se pudo invitar (${r.etapa}).`);
  }
  return r;
}
